<template>
  <div class="container col-12">
    <div class="my-3">
      <div class="d-flex align-center justify-center mb-5">
        <BaseListToolbar
          @search="updateSearchValue"
          title="Interviews"
          hideSearchBar
        />
      </div>
      <ListTypeSelection
        :text="listTypeSelectionText"
        @selectListType="changeTense"
      >
        <template v-slot:icon>
          <v-icon color="primary" class="ml-2">
            mdi-clock-outline
          </v-icon>
        </template>
      </ListTypeSelection>
      <div v-if="isFetchingInterviewSlots">
        <v-skeleton-loader
          v-for="i in 5"
          :key="i"
          type="list-item-three-line"
          class="my-2"
        />
      </div>
      <div v-else>
        <div v-if="isNotEmpty">
          <InterviewListItem
            :showJobTitile="!currentBookingId"
            v-for="(interview, index) in interviewSlots"
            :key="index"
            :interview="interview"
          />
          <v-pagination
            :total-visible="page.pageSize"
            v-model="page.currentPage"
            @input="loadInterviewSlots"
            :length="page.lastPage"
          />
        </div>
        <EmptyStates
          v-else
          icon="interviews-empty-state"
          :description="emptyStateDescription"
          :isFiltered="isFiltered"
        />
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>

<script>
import InterviewListItem from "@/views/bookings/BookingInterviews/components/InterviewListItem";
import ListTypeSelection from "@/components/common/ListTypeSelection";
import { copyState } from "@/utils/helpers";
import { createNamespacedHelpers } from "vuex";
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_INTERVIEW_SLOTS } from "@/store/modules/bookings/interviews/actions";
import EmptyStates from "@/components/common/EmptyStates";
import { size } from "lodash";
import BaseListToolbar from "@/components/common/Toolbar/BaseListToolbar";
import { paginationMixin } from "@/mixins/pagination.mixin";

const { mapActions, mapState } = createNamespacedHelpers(INTERVIEWS_NAMESPACE);

export default {
  name: "BookingInterviews",
  components: {
    ListTypeSelection,
    InterviewListItem,
    EmptyStates,
    BaseListToolbar
  },
  mixins: [paginationMixin],
  props: {
    bookingId: String
  },
  data() {
    return {
      occurrence: "future",
      search: ""
    };
  },
  created() {
    this.loadInterviewSlots();
  },
  computed: {
    ...mapState(copyState(["interviewSlots", "isFetchingInterviewSlots"])),
    listTypeSelectionText() {
      return `${this.timeActionName} Past Interviews`;
    },
    requestParams() {
      return {
        ...this.requestPageParams,
        filter: this.filters
      };
    },
    filters() {
      return {
        occurrence: this.occurrence,
        filled: true,
        ...(this.bookingId && { booking_id: this.bookingId }),
        ...(size(this.search) && { search: this.search })
      };
    },
    areAllInterviewsDisplayed() {
      return this.occurrence === "all";
    },
    timeActionName() {
      return this.areAllInterviewsDisplayed ? "Hide" : "Show";
    },
    currentBookingId() {
      return this.$route.params.id;
    },
    isNotEmpty() {
      return size(this.interviewSlots);
    },
    isFiltered() {
      return Boolean(size(this.search));
    },
    emptyStateDescription() {
      if (this.isFiltered) {
        return "No interviews match your search criteria";
      }
      return this.currentBookingId
        ? this.areAllInterviewsDisplayed
          ? "No interviews have been scheduled for this role"
          : "No interviews have been arranged for this role yet"
        : this.areAllInterviewsDisplayed
        ? "No interviews have been scheduled"
        : "There are no upcoming interviews";
    }
  },
  methods: {
    ...mapActions({
      fetchInterviewSlots: FETCH_INTERVIEW_SLOTS
    }),
    async loadInterviewSlots() {
      const data = await this.fetchInterviewSlots(this.requestParams);
      if (data) {
        this.updatePageDetails(data.meta);
      }
    },
    changeTense() {
      this.occurrence = this.areAllInterviewsDisplayed ? "future" : "all";
      this.resetPagination();
      this.loadInterviewSlots();
    },
    updateSearchValue(text) {
      this.search = text;
      this.loadInterviewSlots();
    }
  }
};
</script>

<style lang="scss"></style>
